import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useAuth } from './AuthContext';
import theme from '../utils/theme';
import adminTheme from '../utils/adminTheme';
import { ThemeMode, getUserThemePreference, setUserThemePreference, getDefaultThemePreference } from '../services/userThemeService';

// Create a dark version of the admin theme
import { createTheme } from '@mui/material/styles';
const darkAdminTheme = createTheme({
  ...adminTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#4CAF50', // Brighter green for dark mode
      light: '#81C784',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#42A5F5', // Brighter blue for dark mode
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#FFFFFF', // Pure white for maximum contrast
      secondary: '#FFFFFF', // Also white for secondary text in dark mode
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
          color: '#FFFFFF',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E1E1E',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.25)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#4CAF50', // Signature green for icons
          minWidth: '40px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          '&.Mui-selected': {
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            color: '#4CAF50',
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.25)',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: '#FFFFFF',
        },
        head: {
          backgroundColor: '#2D2D2D',
          color: '#FFFFFF',
          fontWeight: 'bold',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D2D2D', // Slightly lighter background for cards to create better contrast
          color: '#FFFFFF',
          boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          '&.MuiTypography-colorTextSecondary': {
            color: '#FFFFFF', // Force all secondary text to be white
          },
          '&.MuiTypography-h6, &.MuiTypography-h5, &.MuiTypography-h4, &.MuiTypography-h3, &.MuiTypography-h2, &.MuiTypography-h1': {
            color: '#4CAF50', // Make all headings the signature green
            fontWeight: 500,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#4CAF50',
          color: '#FFFFFF',
          fontWeight: 'bold',
          '&:hover': {
            backgroundColor: '#45a049',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#FFFFFF',
        },
        secondary: {
          color: '#FFFFFF',
        },
      },
    },
  },
});

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  isAdminRoute: boolean;
  setIsAdminRoute: (isAdmin: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

interface CustomThemeProviderProps {
  children: ReactNode;
}

export const CustomThemeProvider: React.FC<CustomThemeProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  // Use email as a unique identifier since both User and DevUser have email
  const userId = currentUser?.email || '';
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getDefaultThemePreference());
  const [isAdminRoute, setIsAdminRoute] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Load user theme preference on mount
  useEffect(() => {
    const loadThemePreference = async () => {
      if (userId) {
        const preference = await getUserThemePreference(userId);
        if (preference) {
          setThemeModeState(preference.mode);
        } else {
          // If no preference is set, use system preference
          setThemeModeState('system');
        }
      } else {
        // If not logged in, use default
        setThemeModeState(getDefaultThemePreference());
      }
      setLoading(false);
    };

    loadThemePreference();
  }, [userId]);

  // Update theme when user changes preference
  const setThemeMode = async (mode: ThemeMode): Promise<void> => {
    setThemeModeState(mode);
    if (userId) {
      await setUserThemePreference(userId, mode);
    }
  };

  // Determine which theme to use based on preferences
  const getActiveTheme = () => {
    // For admin routes
    if (isAdminRoute) {
      if (themeMode === 'system') {
        return prefersDarkMode ? darkAdminTheme : adminTheme;
      }
      return themeMode === 'dark' ? darkAdminTheme : adminTheme;
    }
    
    // For public routes, always use the main theme (which is dark by default)
    return theme;
  };

  const contextValue: ThemeContextType = {
    themeMode,
    setThemeMode,
    isAdminRoute,
    setIsAdminRoute,
  };

  if (loading) {
    // Return a basic provider while loading to avoid flickering
    return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={getActiveTheme()}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 